/**
 * Precast Mounting Parts Tool (BIM: Precast > Connections > Mounting Parts)
 *
 * Click on any precast member (or a monolithic wall/floor that will host
 * embedded hardware) to place the selected embedded part — lifting anchor,
 * handling loop, insert plate, bolt anchor, threaded insert or shear key —
 * flush with the clicked face. The part stores the host face normal so the
 * renderer and the shop-drawing projector keep it correctly oriented.
 * The tool stays active for repeated placement; Esc ends it (BIM pattern).
 */
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from '../BaseTool';
import {
    Element,
    PRECAST_HOST_CATEGORIES,
    PrecastMountingKind,
    PrecastMountingPartElement,
} from '../../core/types';
import { getPrecastConfig } from '../../state/usePrecastConfigStore';
import i18n from '../../i18n';

export class PrecastMountingPartsTool extends BaseTool {
    id = 'precast_mounting_parts';
    name = 'Precast Mounting Parts';
    cursor = 'crosshair';

    private get kind(): PrecastMountingKind {
        return this.context?.bimStore.toolProperties.precastMountingKind ?? 'Lifting Anchor';
    }

    private get sizeMm(): number {
        const fromProp = this.context?.bimStore.toolProperties.precastMountingSize;
        return fromProp && fromProp > 0 ? fromProp : getPrecastConfig().defaultPlateSizeMm;
    }

    onActivate(): void {
        this.notify(i18n.t('tools.notify.pickAFaceOnAPrecast'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) =>
            (PRECAST_HOST_CATEGORIES as readonly string[]).includes(el.category));
        if (!hit) {
            this.notify(i18n.t('tools.notify.mountingPartsPickAPrecastMember'), 'warning');
            return;
        }

        const host = hit.element as Element;
        // Face normal from the hit triangle, lifted into world space.
        const normal = this.surfaceNormal(event, hit.point);
        // Sink the part 2 cm below the surface so it visually embeds.
        const position: [number, number, number] = [
            hit.point.x - normal.x * 0.02,
            hit.point.y - normal.y * 0.02,
            hit.point.z - normal.z * 0.02,
        ];

        const part: PrecastMountingPartElement = {
            id: uuidv4(),
            name: `${this.kind}`,
            category: 'Precast Mounting Parts',
            type: `Precast Mounting: ${this.kind}`,
            parameters: {
                'Host Element Id': host.id,
                'Host Category': host.category,
                'Nominal Size': `${this.sizeMm} mm`,
                'Part Type': this.kind,
            },
            constraints: [],
            geometryData: {
                position,
                normal: [normal.x, normal.y, normal.z],
                rotation: 0,
                hostId: host.id,
                size: this.sizeMm,
                kind: this.kind,
            },
        };

        this.context.bimStore.addElement(part);
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    /**
     * Outward face normal at the picked point. Falls back to the dominant
     * axis when no triangle normal is available (instanced bulk geometry).
     */
    private surfaceNormal(event: ThreeEvent<PointerEvent>, point: THREE.Vector3): THREE.Vector3 {
        const hits = this.raycastSceneAtEvent(event);
        for (const hit of hits) {
            if (!hit.object.visible || hit.object.userData?.isEventPlane) continue;
            const id = this.resolveElementIdFromHit(hit);
            if (!id) continue;
            if (hit.face?.normal) {
                const n = hit.face.normal.clone();
                n.applyQuaternion(new THREE.Quaternion().setFromRotationMatrix(
                    new THREE.Matrix4().extractRotation(hit.object.matrixWorld)
                ));
                return n.normalize();
            }
            // Instanced fallback: approximate the face normal from the host
            // solid's dominant axis relative to the hit point.
            const host = this.context?.bimStore.project.elements.get(id);
            if (host) return this.hostApproximateNormal(host, point);
        }
        return new THREE.Vector3(0, 0, 1);
    }

    private hostApproximateNormal(host: Element, point: THREE.Vector3): THREE.Vector3 {
        const g = host.geometryData as any;
        if (host.category === 'Walls' || host.category === 'Precast Walls') {
            const s = new THREE.Vector3(...g.start), e = new THREE.Vector3(...g.end);
            const dir = e.clone().sub(s).normalize();
            return new THREE.Vector3(-dir.y, dir.x, 0);
        }
        if (host.category === 'Floors' || host.category === 'Precast Floors') {
            return new THREE.Vector3(0, 0, 1);
        }
        return new THREE.Vector3(0, 0, 1);
    }
}
